/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Typography,
  TextField,
  useTheme,
  alpha,
  InputAdornment,
} from '@mui/material';
import { PositionShares } from 'types/optionsCalculator';
import DeleteIcon from '@mui/icons-material/Delete';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useCallback, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';

interface EditableUnderlyingPositionProps {
  shares: PositionShares;
  onEditShares: (updatedShares: Partial<PositionShares>) => void;
  onDeleteShares: () => void;
}

const EditableUnderlyingPosition = ({
  shares,
  onEditShares,
  onDeleteShares,
}: EditableUnderlyingPositionProps) => {
  const theme = useTheme();
  const [localQuantity, setLocalQuantity] = useState(Math.abs(shares.quantity));
  const [localPricePaid, setLocalPricePaid] = useState(shares.pricePaid);

  const serverPosColor = useRecoilValue(positiveTrendColorState);
  const serverNegColor = useRecoilValue(negativeTrendColorState);

  // Update local state when props change
  useEffect(() => {
    setLocalQuantity(Math.abs(shares.quantity));
    setLocalPricePaid(shares.pricePaid);
  }, [shares.quantity, shares.pricePaid]);

  // Debounced handlers
  const debouncedEditShares = useCallback(
    debounce((updatedShares: Partial<PositionShares>) => {
      onEditShares(updatedShares);
    }, 300),
    /* eslint-disable react-hooks/exhaustive-deps */
    [onEditShares],
  );

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setLocalQuantity(value);
      const finalQuantity = shares.quantity > 0 ? value : -value;
      debouncedEditShares({ quantity: finalQuantity });
    }
  };

  const handlePricePaidChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newPrice = parseFloat(event.target.value);
    if (!isNaN(newPrice)) {
      setLocalPricePaid(newPrice);
      debouncedEditShares({ pricePaid: newPrice });
    }
  };

  const handlePositionTypeToggle = () => {
    const newQuantity = shares.quantity > 0 ? -localQuantity : localQuantity;
    onEditShares({ quantity: newQuantity });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 3,
        gap: '16px',
      }}
    >
      <Box sx={{ mr: 3 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handlePositionTypeToggle}
          sx={{
            minWidth: '60px',
            position: 'relative',
            color: theme.palette.getContrastText(
              shares.quantity > 0 ? serverPosColor : serverNegColor,
            ),
            backgroundColor:
              shares.quantity > 0 ? serverPosColor : serverNegColor,
            '&:after': {
              content: '"⇄"',
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              color: theme.palette.getContrastText(
                shares.quantity > 0 ? serverNegColor : serverPosColor,
              ),
              backgroundColor:
                shares.quantity > 0 ? serverNegColor : serverPosColor,
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            },
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 0.2s',
              backgroundColor:
                shares.quantity > 0
                  ? alpha(serverPosColor, 0.9)
                  : alpha(serverNegColor, 0.9),
            },
          }}
        >
          {shares.quantity > 0 ? 'BTO' : 'STO'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mr: 1 }}>
          {shares.sym} Stock
        </Typography>
        <TextField
          value={localQuantity}
          onChange={handleQuantityChange}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">shares</InputAdornment>
            ),
          }}
          sx={{
            width: '120px',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.35),
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '& input': {
              '-moz-appearance': 'textfield',
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
              {
                '-webkit-appearance': 'none',
                margin: 0,
              },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '100px' }}>
        <Typography variant="body2" sx={{ mr: 1 }}>
          Price:
        </Typography>
        <TextField
          value={localPricePaid}
          onChange={handlePricePaidChange}
          size="small"
          type="number"
          inputProps={{ step: 0.01 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{
            width: '100px',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.35),
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '& input': {
              '-moz-appearance': 'textfield',
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
              {
                '-webkit-appearance': 'none',
                margin: 0,
              },
          }}
        />
      </Box>

      <Box sx={{ ml: 'auto' }}>
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={onDeleteShares}
          sx={{ minWidth: 'auto', p: '4px 8px' }}
        >
          <DeleteIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export default EditableUnderlyingPosition;
